import { BRAND } from "constants/brand";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import useUser from "hooks/useUser";
import Select from "components/Ui/SelectField/SelectField";
import { useSelector } from "react-redux";
import useArea from "hooks/useArea";
import { ROLE_LABELS } from "constants/roleLabels";
import { fieldOf } from "utils/formField";

/**
 * The grantable sub-admin permissions.
 *
 * Must stay in step with `manage_*` on the API's `adminRole` model, because the
 * API enforces those columns server-side (403) whether or not this list mentions
 * them. `manage_product_videos` was missing here while `productVideo.js` already
 * required it, so the فيديوهات المنتجات page was unreachable for every
 * sub-admin and un-grantable from this form — a permission that existed only as
 * a rejection.
 *
 * NOT listed, deliberately: `manage_deliveries`. The column exists on the model
 * and is written as `false` by the admin/user creators, but nothing anywhere
 * reads it. A checkbox for it would grant exactly nothing while implying it
 * gated طلبات السائقين (which is actually under `manage_orders`). Give it a
 * checkbox when something enforces it, not before.
 */
const rolesOptions = [
    { label: "ادارة الطلبات", value: "manage_orders" },
    { label: "ادارة المنتجات", value: "manage_products" },
    { label: "ادارة الحسابات", value: "manage_users" },
    { label: "ادارة المدن", value: "manage_cities" },
    { label: "ادارة الفئات", value: "manage_categories" },
    { label: "ادارة المطاعم", value: "manage_vendors" },
    { label: "ادارة التقييمات", value: "manage_reviews" },
    { label: "ادارة عروض التوصيل", value: "manage_delivery_offers" },
    { label: "ادارة فيديوهات المنتجات", value: "manage_product_videos" },
];
const EditUser = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    const { loading, updateUser, error } = useUser();
    const [roles, setRoles] = useState([]);
    const [role, setRole] = useState("");
    // Follows the row being edited, and the type select above updates it live.
    // One shared form serves the admins, drivers and customers pages, so a
    // fixed "مستخدم" here would name the wrong role on two of the three — and
    // "مستخدم" was the OLD word for the customer role. See constants/roleLabels.js.
    const subjectLabel =
        ROLE_LABELS[role || (props.user?.admin?.super_admin ? "super_admin" : props.user?.role)] ||
        "الحساب";
    const adminInfo = useSelector((state) => state.auth.userData);
    const [newAreas, setAreas] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [city, selectCity] = useState("");
    const { areas } = useArea();

    useEffect(() => {
        const isSuperAdmin = props.user?.admin?.super_admin ?? false;
        setRole("");
        if (props.user) {
            selectCity(props.user.cityId);
        }

        if (props.user?.role === "admin" && !isSuperAdmin) {
            setRole("admin");
        }

        if (props.user?.delivery) {
            setRole("delivery");
            setSelectedAreas(
                (props.user.delivery.areas || []).map((area) => {
                    return { value: area.id, label: area.name };
                }),
            );
        }

        if (props.user?.adminRole) {
            const selectedRoles = Object.entries(props.user?.adminRole)
                .filter(([key, value]) => key.startsWith("manage_") && value === true)
                .map(([key]) => ({
                    label: rolesOptions.find((r) => r.value === key)?.label,
                    value: key,
                }));
            setRoles(selectedRoles);
        }
    }, [props.user]);

    useEffect(() => {
        if (areas && city !== "") {
            setAreas(
                areas
                    .filter((a) => +a.cityId === +city)
                    .map((area) => {
                        return { value: area.id, label: area.name };
                    }),
            );
        } else {
            setAreas([]);
        }
    }, [areas, city]);

    const handleChange = (selectedOptions) => {
        setRoles(selectedOptions);
    };

    const handleChangeAreas = (selectedOptions) => {
        setSelectedAreas(selectedOptions);
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        const userData = new FormData();

        selectedAreas.forEach((area) => {
            userData.append("areas[]", area.value);
        });
        // FormData cannot send an empty array. Without this line, a form with
        // no area selected sends no `areas`, and the API keeps the old areas.
        // An empty `areas` tells the API to clear them (api#187).
        if (selectedAreas.length === 0) {
            userData.append("areas", "");
        }

        if (fieldOf(form.current, "image").files[0]) {
            userData.append(
                "image",
                fieldOf(form.current, "image").files[0],
                fieldOf(form.current, "image").files[0].name,
            );
        }
        userData.append("id", props.user.id);
        userData.append("name", fieldOf(form.current, "name").value);
        userData.append("email", fieldOf(form.current, "email").value);
        userData.append("phone", fieldOf(form.current, "phone").value);
        if (fieldOf(form.current, "role").value === "super_admin") {
            userData.append("role", "admin");
            userData.append("super_admin", true);
        } else {
            userData.append("role", fieldOf(form.current, "role").value);
        }

        if (role === "admin") {
            userData.append("roles", JSON.stringify(roles.map((r) => r.value)));
            userData.append("cityId", fieldOf(form.current, "cityId").value);
            userData.append("active", fieldOf(form.current, "active").value);
        } else if (role === "delivery") {
            // Read by name: each branch shows other fields, so a position
            // here meant another field in each branch.
            userData.append("cityId", fieldOf(form.current, "cityId").value);
            userData.append(
                "max_concurrent_orders",
                fieldOf(form.current, "max_concurrent_orders").value,
            );
            userData.append("wallet_enabled", fieldOf(form.current, "wallet_enabled").value);
            userData.append("active", fieldOf(form.current, "active").value);
        } else {
            userData.append("cityId", fieldOf(form.current, "cityId").value);
            userData.append("active", fieldOf(form.current, "active").value);
        }
        try {
            const response = await updateUser(userData);

            // Assuming `response` contains information to check if the operation succeeded

            if (response.name) {
                props.showMessage("success", "تم التعديل", "تم التعديل بنجاح");
                props.close(); // Uncomment if you need to close a modal or similar
            } else {
                props.showMessage(
                    "error",
                    "هناك خطأ",
                    response.response.data.message || "حدث خطأ غير متوقع",
                );
            }
        } catch (err) {
            console.error("Error adding user:", err);
            props.showMessage("error", "هناك خطأ", error || "حدث خطأ غير متوقع");
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>تعديل {subjectLabel}</h5>
                            <FontAwesomeIcon icon={faClose} onClick={props.close} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            <Form.Group className="mb-3" controlId="image">
                                <Form.Label>صوره {subjectLabel}</Form.Label>
                                <Form.Control
                                    name="image"
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.svg,.webp"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>
                                    اسم {subjectLabel}
                                    <span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="name"
                                    type="text"
                                    defaultValue={props.user?.name}
                                    placeholder={`اسم ${subjectLabel}`}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="email">
                                <Form.Label>البريد الالكتروني</Form.Label>
                                <Form.Control
                                    name="email"
                                    type="text"
                                    defaultValue={props.user?.email}
                                    placeholder="البريد"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="phone">
                                <Form.Label>
                                    رقم الهاتف<span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="phone"
                                    type="text"
                                    defaultValue={props.user?.phone}
                                    placeholder="رقم الهاتف"
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="type">
                                <Form.Label>
                                    نوع الحساب<span>*</span>
                                </Form.Label>
                                <Form.Select
                                    name="role"
                                    required
                                    defaultValue={
                                        props.user?.admin?.super_admin
                                            ? "super_admin"
                                            : props.user?.role
                                    }
                                    onChange={(e) => {
                                        setRole(e.target.value);
                                    }}
                                >
                                    <option value={"delivery"}>{ROLE_LABELS.delivery}</option>
                                    <option value={"customer"}>{ROLE_LABELS.customer}</option>
                                    <option value={"vendor"}>{ROLE_LABELS.vendor}</option>
                                    <option value={"admin"}>{ROLE_LABELS.admin}</option>
                                    {adminInfo.super_admin ? (
                                        <option value={"super_admin"}>
                                            {ROLE_LABELS.super_admin}
                                        </option>
                                    ) : null}
                                </Form.Select>
                            </Form.Group>
                            {role === "admin" ? (
                                <Form.Group className="mb-3" controlId="status">
                                    <Form.Label>
                                        الصلاحيات<span style={{ color: "red" }}>*</span>
                                    </Form.Label>
                                    <Select
                                        value={roles}
                                        options={rolesOptions}
                                        isMulti
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            ) : null}
                            <Form.Group className="mb-3" controlId="status">
                                <Form.Label>
                                    المدينه<span>*</span>
                                </Form.Label>
                                <Form.Select
                                    name="cityId"
                                    defaultValue={props.user?.cityId}
                                    onChange={(e) => {
                                        selectCity(e.target.value);
                                    }}
                                >
                                    {props.cities?.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            {role === "delivery" ? (
                                <Form.Group className="mb-3" controlId="status">
                                    <Form.Label>
                                        المناطق<span style={{ color: "red" }}>*</span>
                                    </Form.Label>
                                    <Select
                                        value={selectedAreas}
                                        options={newAreas}
                                        placeholder={"المناطق"}
                                        isMulti
                                        onChange={handleChangeAreas}
                                    />
                                </Form.Group>
                            ) : null}
                            {role === "delivery" ? (
                                <Form.Group className="mb-3" controlId="max-concurrent-orders">
                                    <Form.Label>الحد الأقصى للطلبات المفتوحة</Form.Label>
                                    <Form.Control
                                        name="max_concurrent_orders"
                                        type="number"
                                        min="0"
                                        step="1"
                                        defaultValue={
                                            props.user?.delivery?.max_concurrent_orders ?? ""
                                        }
                                        placeholder="فارغ = بدون حد"
                                    />
                                </Form.Group>
                            ) : null}
                            {role === "delivery" ? (
                                <Form.Group className="mb-3" controlId="wallet-enabled">
                                    <Form.Label>المحفظة</Form.Label>
                                    <Form.Select
                                        name="wallet_enabled"
                                        defaultValue={
                                            props.user?.delivery?.wallet_enabled ? "true" : "false"
                                        }
                                    >
                                        <option value={"false"}>نقدي — يدفع في آخر الشهر</option>
                                        <option value={"true"}>
                                            محفظة — يخصم من رصيده مع كل طلب
                                        </option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        السائق على المحفظة لا يستلم أي طلب برصيد صفر. الخصم يتم عند
                                        القبول، وقيمته من إعدادات المدينة.
                                    </Form.Text>
                                </Form.Group>
                            ) : null}
                            <Form.Group className="mb-3" controlId="active">
                                <Form.Label>
                                    حظر<span>*</span>
                                </Form.Label>
                                <Form.Select
                                    name="active"
                                    required
                                    defaultValue={props.user?.active}
                                >
                                    <option value={"true"}>غير محظور</option>
                                    <option value={"false"}>محظور</option>
                                </Form.Select>
                            </Form.Group>
                            <div className="buttons">
                                <button className="cancel" onClick={props.close} type="button">
                                    إلغاء
                                </button>
                                <button className="button" type="submit">
                                    تعديل {subjectLabel}
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};
export default EditUser;
